import { useEffect, useState } from "react";
import { chain, merge } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import {
  saveValue,
  assetSelector,
  loanDetailsSelector,
  entitySelector,
  customersSelector,
  declarationSelector,
} from "../store/slices/applicationFormSlice";
import products from "../products";
import calculateRepayments from "src/utils/calculateRepayments";
import {
  evaluateCriteria,
  calculateLoading,
  calculateFees,
  calculateExtras,
} from "src/utils/evaluateCriteria";
import getMonthlyRate from "src/utils/getMonthlyRate";
import Product from "./Product";

export default function ProductSelector({ isApplicationComplete }) {
  const dispatch = useDispatch();

  const asset = useSelector(assetSelector);
  const loanDetails = useSelector(loanDetailsSelector);
  const entity = useSelector(entitySelector);
  const customers = useSelector(customersSelector);
  const declaration = useSelector(declarationSelector);

  const {
    assetValue = 0,
    ageOfAsset = "",
    assetType = "",
    privateSale,
    typeOfSale,
    condition,
  } = useSelector(assetSelector);

  const {
    balloon = 0,
    term = 0,
    brokerage = 0,
    rate = 0,
    deposit = 0,
    depositAmount = 0,
    fees
  } = useSelector(loanDetailsSelector);

  const [productsMap, setProductsMap] = useState([]);
  const netAssetValue = assetValue - depositAmount
  const orderByParametersList = [
    ["qualification", "repayments"],
    ["asc", "asc"],
  ]

  const BALLOON = balloon / 100 * netAssetValue

  const handleLoanAmount = (fees, brokerage) => {
    const dep = (deposit / 100 * netAssetValue)
    const brok = netAssetValue * (brokerage / 100)
    return netAssetValue + brok + fees
  };

  function evaluateProducts() {
    const firstCustomer = customers?.[0]
    const customerProfile = customers?.[0]?.customerProfile
    const creditProfile = customers?.map((customer) => customer.creditProfile);

    const productList = products?.map((product) => {
      return product?.productTiers?.map((tier) => {
        const defaultBrokerage = calculateExtras(product.brokerage || 0, {
          asset,
          loanDetails,
          entity,
        })[0];

        const customBrokerage = brokerage;
        if (customBrokerage && defaultBrokerage?.value) {
          defaultBrokerage.value = customBrokerage
        }

        const FEES = fees?.[0] ? fees : product?.fees

        const feesList = calculateFees(FEES, {
          asset,
          loanDetails,
          entity,
          customer: firstCustomer,
          customerProfile,
          creditProfile,
        });

        const feesTotal = feesList.reduce(
          (accum, item) => item.value + accum,
          0
        );

        const loanAmount = handleLoanAmount(
          feesTotal,
          defaultBrokerage?.value || 0
        );

        const loadingList = calculateLoading(product.loading, {
          asset,
          loanDetails: {
            ...loanDetails,
            ...{
              loanAmount,
              brokerageRate: defaultBrokerage?.value,
              deposit: 100 - ((assetValue - deposit) / assetValue) * 100,
            },
          },
          entity,
          customerProfile,
          declaration,
        });

        const loadingTotal = loadingList.reduce((accum, item) => {
          return item.result === true ? item.value + accum : accum;
        }, 0);

        const loadedRate = tier.rate + loadingTotal;

        const quoteDetails = calculateQuote(
          loanAmount,
          BALLOON,
          term,
          loadedRate
        );

        const LOAN_DETAILS = {
          ...loanDetails,
          ...{
            loanAmount: netAssetValue,
            repayments: quoteDetails.repayments,
          },
        };

        const productDetails = {
          lender: product.lender,
          productName: tier.name,
          name: tier.name,
          rate: loadedRate.toFixed(2),
          excludedRequiredFields: tier?.excludedRequiredFields
        };

        const criteriaResults = evaluateCriteria(tier.criteria, {
          asset,
          loanDetails: LOAN_DETAILS,
          entity,
          customer: firstCustomer,
          customerProfile,
          creditProfile
        }).filter((result) => {
          if (!result.criteria) return result;
          if (result.criteria.every((criteria) => criteria.result)) return result;
          if (
            !result.criteria.some((criteria) => criteria.result) &&
            result.criteria.some((item) => result.primary === item.attribute)
          ) {
            return result.criteria.find((item) => result.primary === item.attribute);
          }
          return result;
        });

        const overallResult = criteriaResults.reduce((count, item) => {
          return item?.result ? ++count : count;
        }, 0);

        return merge(productDetails, {
          loanAmount: 1 * loanAmount,
          repayments: quoteDetails?.repayments,
          brokerage: defaultBrokerage || 0,
          fees: feesList,
          loading: { loadingList, loadingTotal },
          criteriaResults,
          fit: (overallResult / criteriaResults.length) * 100,
          criteria: `${overallResult}/${criteriaResults.length}`,
          qualification: criteriaResults.length - overallResult,
          term,
          assetValue: netAssetValue,
          deposit: (deposit / 100 * assetValue),
        });
      });
    });

    const list = chain(productList)
      .flatten()
      .filter((product) => {
        const assetTypeCriteria = product.criteriaResults.find((result) => {
          return result?.attribute === "assetType";
        });

        return (
          assetTypeCriteria?.result === true ||
          assetTypeCriteria?.result === undefined
        );
      })
      .filter((product) => {
        const ageOfAssetCriteria = product.criteriaResults.find((result) => {
          return result?.attribute === "ageOfAsset";
        });

        return (
          ageOfAssetCriteria?.result === true ||
          ageOfAssetCriteria?.result === undefined
        );
      })
      .filter((product) => {
        const livingArrangementsCriteria = product.criteriaResults.find((result) => {
          return result?.attribute === "livingArrangements";
        });

        return (
          livingArrangementsCriteria?.result === true ||
          livingArrangementsCriteria?.result === undefined
        );
      })
      .orderBy(...orderByParametersList)
      .value();

    dispatch(saveValue({
      excludedRequiredFields: list[0]?.excludedRequiredFields
    }))
    setProductsMap([list?.[0]]);
  }

  const calculateQuote = (loanAmount, balloon, term, rate) => {
    const monthlyRate = getMonthlyRate(rate);
    const rpmts = calculateRepayments(monthlyRate, term, -loanAmount, balloon, 1);
    const roundedRepayments = (Math.round(rpmts * 100) / 100).toFixed(2);

    return {
      repayments: roundedRepayments * 1,
    };
  };


  useEffect(() => {
    evaluateProducts();
  }, [
    rate,
    brokerage,
    assetValue,
    deposit,
    assetType,
    privateSale,
    ageOfAsset,
    balloon,
    term,
    customers,
    typeOfSale,
    condition,
    fees,
    entity,
    declaration
  ]);


  return (
    <>
      {productsMap?.length > 0 && productsMap?.map((productDetails, index) => (
        <Product key={index} productDetails={productDetails} isApplicationComplete={isApplicationComplete} />
      ))}
    </>
  );
}